/**
 * SRES · Fleet-level Rollback Analytics card (Iter98i · Feb 2026).
 *
 * Cross-project view of every safe-autofix rollback across the fleet.
 * Surfaces systemic auto-fix problems admins would otherwise miss.
 *
 * Iter98k · Monthly bars are click-through → open a drill-down modal
 * listing every rollback recorded in that month, with per-row
 * jump-to-project links.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X, ArrowSquareOut, DownloadSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function FleetRollbackCard() {
  // Iter98j · Filter state — commodity / region / cp segments.
  const [filters, setFilters] = useState({ commodity: "", region: "", cp: "" });
  // Iter98k · Drill-down modal — the YYYY-MM the user clicked.
  const [drillMonth, setDrillMonth] = useState(null);

  const optsQ = useQuery({
    queryKey: ["fleet-autofix-rollback-filters"],
    queryFn:  () => api.get("/admin/autofix-rollback-analytics/filters").then((r) => r.data),
    staleTime: 60_000,
    retry: 0,
  });
  const q = useQuery({
    queryKey: ["fleet-autofix-rollback-analytics", filters],
    queryFn:  () => {
      const p = new URLSearchParams();
      for (const k of ["commodity", "region", "cp"]) if (filters[k]) p.set(k, filters[k]);
      const qs = p.toString();
      return api.get(`/admin/autofix-rollback-analytics${qs ? "?" + qs : ""}`).then((r) => r.data);
    },
    staleTime: 30_000,
    retry: 0,
  });
  const d = q.data;
  if (!d || d.total_runs === 0) return null;
  const months = d.months || [];
  const maxTotal = Math.max(1, ...months.map((m) => m.total));
  const opts = optsQ.data || {};

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4"
             data-testid="fleet-rollback-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">
          Fleet · Rollback trend
        </div>
        {/* Filter dropdowns — visible only when there is any option */}
        {(opts.commodities?.length || 0) > 0 && (
          <select
            value={filters.commodity}
            onChange={(e) => setFilters((f) => ({ ...f, commodity: e.target.value }))}
            className="text-[10.5px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
            data-testid="fleet-rollback-filter-commodity">
            <option value="">All commodities</option>
            {opts.commodities.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        {(opts.regions?.length || 0) > 0 && (
          <select
            value={filters.region}
            onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
            className="text-[10.5px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
            data-testid="fleet-rollback-filter-region">
            <option value="">All regions</option>
            {opts.regions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        {(opts.cps?.length || 0) > 0 && (
          <select
            value={filters.cp}
            onChange={(e) => setFilters((f) => ({ ...f, cp: e.target.value }))}
            className="text-[10.5px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
            data-testid="fleet-rollback-filter-cp">
            <option value="">All CPs</option>
            {opts.cps.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        <span className="ml-auto text-[10.5px] font-mono text-slate-500"
                 data-testid="fleet-rollback-totals">
          {d.total_runs} run{d.total_runs === 1 ? "" : "s"} · {d.total_docs} record{d.total_docs === 1 ? "" : "s"} restored
        </span>
      </div>
      {/* Monthly bar chart (SVG-free, pure divs). Iter98k · click-through. */}
      <div className="flex items-end gap-1.5 h-20 mb-3" role="img"
             aria-label="Monthly fleet rollback counts">
        {months.map((m) => (
          <button
            key={m.month}
            type="button"
            className="flex-1 flex flex-col items-center gap-0.5 group focus:outline-none focus:ring-2 focus:ring-brand-blue/40 rounded"
            title={`Click to see ${m.total} rollback${m.total === 1 ? "" : "s"} in ${m.month}`}
            onClick={() => setDrillMonth(m.month)}
            data-testid={`fleet-rollback-bar-${m.month}`}>
            <div className="w-full rounded-sm bg-gradient-to-t from-rose-500 to-rose-400 group-hover:from-rose-600 group-hover:to-rose-500 transition-colors"
                    style={{ height: `${Math.max(2, (m.total / maxTotal) * 56)}px` }} />
            <div className="text-[9px] font-mono text-slate-500 group-hover:text-slate-800">
              {m.month.slice(2, 7)}
            </div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {/* Top projects */}
        <div>
          <div className="text-[9.5px] uppercase tracking-widest text-slate-500 mb-1">
            Top projects
          </div>
          <ul className="text-[11px] space-y-0.5">
            {(d.top_projects || []).slice(0, 5).map((p, i) => (
              <li key={p.project_id}
                    data-testid={`fleet-rollback-project-${i}`}
                    className="flex items-center gap-2">
                <span className="flex-1 text-slate-700 truncate" title={p.name}>{p.name}</span>
                <span className="font-mono text-slate-500">×{p.count}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Top reasons */}
        <div>
          <div className="text-[9.5px] uppercase tracking-widest text-slate-500 mb-1">
            Top reasons
          </div>
          <ul className="text-[11px] space-y-0.5">
            {(d.top_reasons || []).slice(0, 5).map((r, i) => (
              <li key={i}
                    data-testid={`fleet-rollback-reason-${i}`}
                    className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-rose-400/70 shrink-0" />
                <span className="flex-1 text-slate-700 truncate" title={r.reason}>{r.reason}</span>
                <span className="font-mono text-slate-500">×{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {drillMonth && (
        <FleetRollbackDrillModal
          month={drillMonth}
          filters={filters}
          onClose={() => setDrillMonth(null)}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Iter98k · Drill-down modal
// ─────────────────────────────────────────────────────────────────────
function FleetRollbackDrillModal({ month, filters, onClose }) {
  const q = useQuery({
    queryKey: ["fleet-autofix-rollback-month", month, filters],
    queryFn: () => {
      const p = new URLSearchParams({ month });
      for (const k of ["commodity", "region", "cp"]) if (filters[k]) p.set(k, filters[k]);
      return api.get(`/admin/autofix-rollback-analytics/month?${p.toString()}`)
                 .then((r) => r.data);
    },
    retry: 0,
  });

  return (
    <div role="dialog" aria-modal="true"
             data-testid="fleet-rollback-drill-modal"
             className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4"
             onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-3xl max-h-[85vh] rounded-lg bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Fleet · rollback drill-down
            </div>
            <h3 className="mt-0.5 font-heading font-bold text-lg tracking-tight text-slate-900"
                    data-testid="fleet-rollback-drill-title">
              Rollbacks in {month}
            </h3>
            {(filters.commodity || filters.region || filters.cp) && (
              <div className="mt-1 flex flex-wrap gap-1 text-[10.5px]">
                {filters.commodity && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    commodity · {filters.commodity}
                  </span>
                )}
                {filters.region && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    region · {filters.region}
                  </span>
                )}
                {filters.cp && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    cp · {filters.cp}
                  </span>
                )}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose}
                       className="text-slate-500 hover:text-slate-900 rounded p-1"
                       data-testid="fleet-rollback-drill-close"
                       aria-label="Close drill-down">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {q.isLoading && (
            <div className="p-6 text-[12px] text-slate-500 animate-pulse"
                     data-testid="fleet-rollback-drill-loading">
              Loading rollbacks for {month}…
            </div>
          )}
          {q.isError && (
            <div className="p-6 text-[12px] text-rose-700"
                     data-testid="fleet-rollback-drill-error">
              Unable to load rollbacks for {month}. Please try again.
            </div>
          )}
          {!q.isLoading && !q.isError && (q.data?.count || 0) === 0 && (
            <div className="p-6 text-center text-[12px] text-slate-500"
                     data-testid="fleet-rollback-drill-empty">
              No rollbacks recorded in {month} for the current filters.
            </div>
          )}
          {!q.isLoading && (q.data?.count || 0) > 0 && (
            <div className="table-wrapper">
              <table className="w-full text-[11.5px]"
                         data-testid="fleet-rollback-drill-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-widest text-[9.5px]">
                  <tr>
                    <th className="text-left px-4 py-2">Rolled back</th>
                    <th className="text-left px-4 py-2">Project</th>
                    <th className="text-left px-4 py-2" data-mobile-hide="sm">Reason</th>
                    <th className="text-right px-4 py-2" data-mobile-hide="xs">Docs</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data.entries || []).map((e, i) => (
                    <tr key={`${e.run_id}-${i}`}
                          className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`fleet-rollback-drill-row-${i}`}>
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap font-mono text-[10.5px]">
                        {String(e.rolled_back_at || "").slice(0, 19).replace("T", " ")}
                        {e.rolled_back_by && (
                          <div className="text-[9.5px] text-slate-400">by {e.rolled_back_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-800 max-w-[220px] truncate"
                              title={e.project_name}>
                        {e.project_name || e.project_id?.slice(0, 8) || "—"}
                        {(e.commodity || e.region) && (
                          <div className="text-[9.5px] text-slate-500">
                            {[e.commodity, e.region].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-700 max-w-[280px]" data-mobile-hide="sm">
                        <span className="italic" title={e.rolled_back_reason || undefined}>
                          {e.rolled_back_reason || <span className="text-slate-400">no reason recorded</span>}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-600" data-mobile-hide="xs">
                        {e.applied || 0}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {e.project_id && (
                          <Link
                            to={`/app/projects/${e.project_id}/setup`}
                            onClick={onClose}
                            className="inline-flex items-center gap-1 rounded border border-brand-blue text-brand-blue px-2 py-0.5 text-[10.5px] font-semibold hover:bg-brand-blue hover:text-white transition-colors"
                            data-testid={`fleet-rollback-drill-jump-${i}`}
                            title={`Open project ${e.project_name}`}>
                            Open
                            <ArrowSquareOut size={11} />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-2 border-t border-slate-100 bg-slate-50 text-[10.5px] font-mono text-slate-500 flex items-center justify-between"
                 data-testid="fleet-rollback-drill-footer">
          <span>{q.data?.count || 0} rollback{q.data?.count === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!q.data?.count}
              onClick={async () => {
                const p = new URLSearchParams({ month });
                for (const k of ["commodity", "region", "cp"]) if (filters[k]) p.set(k, filters[k]);
                try {
                  const res = await api.get(
                    `/admin/autofix-rollback-analytics/month.csv?${p.toString()}`,
                    { responseType: "blob", timeout: 60_000 },
                  );
                  const filename =
                    res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "")
                    || `fleet_rollbacks_${month}.csv`;
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = filename;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch {
                  // eslint-disable-next-line no-alert
                  alert("CSV export failed. Please retry.");
                }
              }}
              className="inline-flex items-center gap-1 rounded border border-brand-blue bg-white px-2.5 py-1 text-brand-blue font-semibold text-[10.5px] hover:bg-brand-blue hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="fleet-rollback-drill-export-csv"
              title="Download this month's rollbacks as CSV">
              <DownloadSimple size={12} />
              Export CSV
            </button>
            <button type="button" onClick={onClose}
                         className="rounded border border-slate-300 bg-white px-3 py-1 text-slate-600 hover:bg-slate-100">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
